import Title from "common/Typography/Title";
import TargetCard from "features/signals/SignalDrawer/Targets/TargetsSection/TargetCard";
import Flex from "common/Boxes/Flex";
import AddOrderButton from "features/trades/common/AddOrderButton";
import CardSorters from "common/CardSorters/CardSorters";
import React, { useContext, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  newLookSortersStyle,
  OrdersCount,
  OrdersTitle,
} from "features/trades/NewLook/drawerDetails.ts";
import { TargetModal } from "features/signals/EditSignal/TargetModal";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import OrderSectionErrorsSummary from "features/trades/common/OrderSectionErrorsSummary";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { sumBy } from "lodash";
import { toFixed } from "@shared/utils/numbers";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  OrderStatus,
  GetExchangeSignalByIdRequest,
  LanguageNameSpaces,
} from "@shared/types";
import { ORDER_TYPE_TO_TITLE_DISPLAY } from "@shared/common/constants";
import { isEditableTargetZone } from "@shared/features/signals/common/utils";
import {
  EditTargetData,
  EditTargetOrZone,
} from "@shared/features/signals/EditSignal/types";
import { orderHasStatus } from "@shared/features/trades/common/utils";
import { getNumOrdersValidationMessage } from "@shared/features/tradingTerminal/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TargetsListProps {
  targets: EditTargetOrZone[];
  targetType: OrderTypes;
  args: GetExchangeSignalByIdRequest;
  isEdit: boolean;
  setTargets: (targets: EditTargetOrZone[]) => void;
}

const TargetsList = ({
  targets,
  targetType,
  isEdit,
  setTargets,
}: TargetsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  const { isNewLook } = theme;
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isNewLookRow = isNewLook && !isMobile;
  const { t: tTradingTerminal } = useTypedTranslation(
    LanguageNameSpaces.TRADING_TERMINAL
  );
  const [targetModalOpen, setTargetModalOpen] = useState(false);

  const {
    addTarget,
    targetsErrors,
    precisionConfig,
    onValuesModified,
    signalData,
  } = useContext(EditSignalContext)!;

  const errors = targetsErrors[targetType];
  const columnNames = [t("targets_list.ratio"), t("targets_list.price")];
  const columnSpans = [12, 12];
  const targetHasPriceZone = targets.some((target) =>
    isEditableTargetZone(target)
  );
  const targetOrZone = targetHasPriceZone
    ? t("targets.zone")
    : t("targets.target");
  const titles = {
    [OrderTypes.ENTRY]: t("targets.entry", { targetOrZone }),
    [OrderTypes.TAKE_PROFIT]: t("targets.take_profit", {
      targetOrZone,
    }),
    [OrderTypes.STOP]: t("targets.stop", { targetOrZone }),
  };

  const getAddTargetWarningMessage = () => {
    if (targetHasPriceZone) return t("edit_signal.errors.cannot_add_with_zone");

    const numOrdersValidationMessage = getNumOrdersValidationMessage(
      targets.filter(
        (target) => !orderHasStatus(target, true, OrderStatus.CANCELLED)
      ).length,
      targetType,
      signalData?.breakout,
      signalData?.limits,
      true
    );
    if (numOrdersValidationMessage) return numOrdersValidationMessage;

    if (targetType === OrderTypes.ENTRY) {
      const hasPendingTargets = targets.some((target) =>
        orderHasStatus(target, false, OrderStatus.PENDING)
      );
      if (!hasPendingTargets)
        return t("edit_signal.errors.cannot_add_entry_fulfilled");
    }
  };

  const resolveIssues = () => {
    const recalculateTargetRatio = (currentTarget: EditTargetData) => {
      const relativePartOfRatio =
        (currentTarget.edit_ratio! * availableRatio) / totalPendingRatio;

      currentTarget.edit_ratio = toFixed(
        relativePartOfRatio,
        precisionConfig?.percent || 4
      );
    };
    const sumTargetsRatio = (targets: EditTargetData[]) =>
      sumBy(targets, (current) => current.edit_ratio || 0);

    const targetsWithRatio = targets.filter(
      (target) => !isEditableTargetZone(target) && target.edit_ratio
    ) as EditTargetData[];
    const pendingTargets = targetsWithRatio.filter((current) =>
      orderHasStatus(current, false, OrderStatus.PENDING)
    );
    const fulfilledTargets = targetsWithRatio.filter((currentTarget) =>
      orderHasStatus(currentTarget, false, OrderStatus.FULFILLED)
    );

    let totalPendingRatio = sumTargetsRatio(pendingTargets);
    const totalFulfilledRatio = sumTargetsRatio(fulfilledTargets);
    const availableRatio = 1 - totalFulfilledRatio;
    pendingTargets.forEach(recalculateTargetRatio);
    totalPendingRatio = sumTargetsRatio(pendingTargets);
    const totalRatio = totalPendingRatio + totalFulfilledRatio;
    if (totalRatio > 1 && pendingTargets?.[0]?.edit_ratio) {
      pendingTargets[0].edit_ratio -= totalRatio - 1;
    }

    const targetsModified = pendingTargets.some(
      (target) => target.ratio !== target.edit_ratio
    );
    if (targetsModified) onValuesModified();
    setTargets([...targets]);
  };
  const errorsSummary = isEdit && Object.keys(errors.section).length > 0 && (
    <OrderSectionErrorsSummary
      errors={errors.section}
      orderType={targetType}
      resolveIssues={resolveIssues}
      isSignal={true}
    />
  );
  const targetModal = targetModalOpen && (
    <TargetModal
      targetType={targetType}
      title={t("targets_list.add_target_title", {
        targetType: tTradingTerminal(ORDER_TYPE_TO_TITLE_DISPLAY[targetType]),
      })}
      closeModal={() => setTargetModalOpen(false)}
      onOk={(newTarget) => {
        addTarget(targetType, newTarget as EditTargetData);
      }}
    />
  );

  const addTargetButton = isEdit && (
    <AddOrderButton
      addOrder={() => setTargetModalOpen(true)}
      disabledMessage={getAddTargetWarningMessage()}
    />
  );

  return (
    <Wrapper>
      {isNewLook ? (
        <NewLookHead>
          <OrdersTitle>
            {titles[targetType]}
            {!!targets.length && <OrdersCount>{targets.length}</OrdersCount>}
          </OrdersTitle>
          {addTargetButton}
        </NewLookHead>
      ) : (
        <Flex>
          <Title level={4} style={{ marginBottom: 16 }}>
            {titles[targetType]}
          </Title>
          {addTargetButton}
        </Flex>
      )}
      {errorsSummary}
      {(!isNewLook || !!targets.length) && (
        <CardSorters
          columnNames={columnNames}
          columnSpans={isNewLookRow ? NEW_LOOK_COLUMN_SPANS : columnSpans}
          style={
            isNewLook
              ? newLookSortersStyle(theme)
              : { padding: "6px 16px", marginBottom: 12 }
          }
          showExpandCollapse={false}
        />
      )}
      <Targets $isNewLook={isNewLook}>
        {targets.map((target, index: number) => (
          <TargetCard
            key={index}
            target={target}
            index={index}
            targetType={targetType}
            columnSpans={isNewLookRow ? NEW_LOOK_COLUMN_SPANS : columnSpans}
            isMobile={isMobile}
          />
        ))}
      </Targets>
      {targetModal}
    </Wrapper>
  );
};

export default TargetsList;

const Wrapper = styled.div`
  width: 100%;
`;
const NewLookHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Targets = styled.div<{ $isNewLook?: boolean }>`
  ${(p) =>
    p.$isNewLook &&
    `
    display: flex;
    flex-direction: column;
    gap: 8px;
  `}
`;
// Ratio and price, leaving the row's right side to the status.
const NEW_LOOK_COLUMN_SPANS = [8, 9];
